import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// Во всём приложении БД хранит время со сдвигом +7ч относительно локального
// (см. formatAbsoluteDate / formatDate в других компонентах: они вычитают 7 часов).
// Чтобы относительное время и «свежесть» считались корректно, таймстамп надо
// приводить к тому же виду — иначе он уходит в будущее и получается «через 6 ч».
const TZ_OFFSET_HOURS = 7;
const TZ_OFFSET_MS = TZ_OFFSET_HOURS * 60 * 60 * 1000;

// Единая коррекция: ms, эквивалентный тому, что показывается в абсолютной дате.
// null/невалид -> null.
function toCorrectedMs(dateString) {
  if (!dateString) return null;
  const ts = new Date(dateString).getTime();
  if (Number.isNaN(ts)) return null;
  return ts - TZ_OFFSET_MS;
}

// Абсолютная дата в локальном виде.
// Несёт «что именно произошло» — показывается в тултипе и используется как fallback.
export const formatAbsoluteDate = (dateString) => {
  if (!dateString) return 'Нет данных';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Нет данных';

  // Приводим к локальному виду с тем же сдвигом -7ч, что и во всём приложении.
  const local = new Date(date.getTime());
  local.setHours(local.getHours() - TZ_OFFSET_HOURS);

  const year = local.getFullYear();
  const month = String(local.getMonth() + 1).padStart(2, '0');
  const day = String(local.getDate()).padStart(2, '0');
  const hours = String(local.getHours()).padStart(2, '0');
  const minutes = String(local.getMinutes()).padStart(2, '0');
  const seconds = String(local.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// «12 сек назад» / «5 мин назад» / «3 ч назад» / «2 д назад».
export const formatRelative = (dateString, nowMs = Date.now()) => {
  if (!dateString) return 'нет данных';

  const ts = toCorrectedMs(dateString);
  if (ts == null) return 'нет данных';

  const diffMs = nowMs - ts;
  const past = diffMs >= 0;
  const absSec = Math.abs(diffMs) / 1000;

  let text;
  if (absSec < 60) text = `${Math.floor(absSec)} сек`;
  else if (absSec < 3600) text = `${Math.floor(absSec / 60)} мин`;
  else if (absSec < 86400) text = `${Math.floor(absSec / 3600)} ч`;
  else if (absSec < 2592000) text = `${Math.floor(absSec / 86400)} д`;
  else {
    // Дальше месяца относительный счётчик теряет смысл — покажем дату.
    return formatAbsoluteDate(dateString);
  }

  return past ? `${text} назад` : `через ${text}`;
};

// Свежесть по возрасту замера: свежий → норма → устаревший → старый.
// Возвращает { color (MUI palette), title }.
export const getFreshnessLevel = (dateString, nowMs = Date.now()) => {
  if (!dateString) return { color: 'grey', title: 'Нет данных' };

  const ts = toCorrectedMs(dateString);
  if (ts == null) return { color: 'grey', title: 'Нет данных' };

  const ageMin = (nowMs - ts) / 60000;

  if (ageMin < 0) return { color: 'info', title: 'Время из будущего' };
  // <= 15 минут — «горит», данные живые.
  if (ageMin <= 15) return { color: 'success', title: 'Свежие данные' };
  // <= 1 часа — ещё актуально.
  if (ageMin <= 60) return { color: 'info', title: 'Актуальные данные' };
  // <= 24 часов — внимание, возможно проблема со сбором.
  if (ageMin <= 1440) return { color: 'warning', title: 'Данные устаревают' };
  // > 24 часов — старые, красный.
  return { color: 'error', title: 'Устаревшие данные' };
};

// Компонент: цветной индикатор-точка + относительное время, в тултипе — абсолютное.
// Самопересчитывается раз в секунду, чтобы «12 сек назад» действительно тикало.
const RelativeTime = ({ date, variant = 'body2', showDot = true }) => {
  const [, setTick] = useState(0);

  // Тик раз в секунду для коротких значений («сек назад»),
  // иначе реже — незачем дёргать рендер ради «2 д назад».
  useEffect(() => {
    const ts = toCorrectedMs(date);
    if (ts == null) return undefined;
    const ageMin = (Date.now() - ts) / 60000;
    const interval = ageMin < 1 ? 1000 : ageMin < 60 ? 15000 : 60000;
    const id = setInterval(() => setTick((n) => n + 1), interval);
    return () => clearInterval(id);
  }, [date]);

  const freshness = getFreshnessLevel(date);
  const relative = formatRelative(date);
  const absolute = formatAbsoluteDate(date);

  return (
    <Tooltip title={`${absolute} — ${freshness.title}`} arrow placement="left">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {showDot && (
          <FiberManualRecordIcon
            sx={{
              fontSize: 12,
              color: `${freshness.color}.main`,
            }}
          />
        )}
        <Typography variant={variant} component="span">
          {relative}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default RelativeTime;
